import DateFormat from '../utilities/date-format';

/**
 * Library links:
 * 	- https://github.com/atmist/snazzy-info-window
 * 	- https://developers.google.com/maps/documentation/javascript/
 */
export default class CategoryFilter {
	/** @type {HTMLElement} */
	#holderElement;

	/** @type {Array<any>} */
	#data;

	/** @type {String} */
	#type;

	/** @type {Array<any>} */
	#categories = [];

	/** @type {HTMLElement} */
	#categoryListParentElement;

	/** @type {HTMLElement} */
	#categoryContentParentElement;

	/** @type {String} */
	#currentCategoryIconElement;

	/** @type {String} */
	#allCategoryContent;

	/** @type {Number} */
	#featuredBlogID = 0;

	/** @type {google.maps.LatLngBounds} */
	#bounds;

	/** @type {google.maps.Map} */
	#googleMap;

	/** @type {any} */
	#markerGroups = {0: []};

	/** @type {Number} */
	#previewBlockRowIndex = 0;

	/** @type {Number} */
	#previewBlockItemIndex = 0;

	/** @type {Boolean} */
	#isDesktop = window.innerWidth > 992;

	#isTablet = window.innerWidth <= 992 && window.innerWidth > 480;

	/** @type {Number} */
	#maxPreviewBlocksInRow = this.#isDesktop ? 4 : this.#isTablet ? 2 : 1;

	/** @type {HTMLElement} */
	#selectedPreviewBlockRowElement;

	/** @type {HTMLElement} */
	#selectedInfoBoxRowElement;

	/** @type {String} */
	#newPreviewBlockRowElement;

	/** @type {String} */
	#newInfoBoxRowElement;

	/** @type {Array<any>} */
	#previewBlockStorage = [];

	static PLACES_TYPE = 'places';
	static OFFER_TYPE = 'offers';
	static BLOG_TYPE = 'blog';

	static FAQS_TYPE = 'faqs';

	/**
	 * @param {HTMLElement} holderElement
	 * @param {any} data
	 * @param {String} type
	 */
	constructor(holderElement, data, type) {
		this.#holderElement = holderElement;

		if (this.#holderElement.classList.contains('mobile-view') || this.#holderElement.classList.contains('desktop-view')) {
			if (!this.#holderElement.querySelector('h1')) {
				let content = this.#holderElement.querySelectorAll('.content')[0];
				let h1 = document.createElement('h1');
				h1.innerHTML = content.querySelector('h4').innerHTML;
				content.replaceChild(h1, content.querySelector('h4'));
			}
		} else if (!document.querySelector('h1')) {
			let content = this.#holderElement.querySelectorAll('.content')[0];
			let h1 = document.createElement('h1');
			h1.innerHTML = content.querySelector('h4').innerHTML;
			content.replaceChild(h1, content.querySelector('h4'));
		}

		this.#data = data;
		this.#type = type;
		this.#categoryListParentElement = this.#holderElement.querySelector('.category-list');
		this.#categoryContentParentElement = this.#holderElement.querySelector('.category-content');
		this.#allCategoryContent = this.#holderElement.querySelector('.all-category-content').innerHTML;
		this.#currentCategoryIconElement = '<img src="' + window.config.themeDirectory + '/images/svg/arrow-left-dark.svg" alt="Current category" class="current-category-icon">';
		if (this.#type === CategoryFilter.PLACES_TYPE) {
			this.#bounds = new google.maps.LatLngBounds();
			this.#googleMap = new google.maps.Map(this.#holderElement.querySelector('#google-map'),{
				zoom: 11,
				styles: [{"featureType": "all", "elementType": "labels.text", "stylers": [{"color": "#878787"}]}, {"featureType": "all", "elementType": "labels.text.stroke", "stylers": [{"visibility": "off"}]},{"featureType": "landscape", "elementType": "all", "stylers": [{"color": "#f9f5ed"}]},{"featureType": "road.highway", "elementType": "all", "stylers": [{"color": "#f5f5f5"}]}, {"featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{"color": "#c9c9c9"}]}, {"featureType": "water", "elementType": "all", "stylers": [{"color": "#aee0f4"}]}],
				disableDefaultUI: true
			});
		}
		this.#selectedPreviewBlockRowElement = this.#holderElement.querySelector('.preview-block-row-' + this.#previewBlockRowIndex.toString());
		this.#selectedInfoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + this.#previewBlockRowIndex.toString());
		this.onLoad();
	}

	onLoad() {
		if (this.#type !== CategoryFilter.FAQS_TYPE) {
			this.#setCategories();
		}
		if (this.#type === CategoryFilter.PLACES_TYPE) {
			this.#initialiseMarkers();
		}
		if (this.#type === CategoryFilter.BLOG_TYPE) {
			this.#getFeaturedBlogID();
		}
		this.renderCategoryItems();
		this.#handleCategoryItems();
		this.#handleData();
	}


	#setCategories() {
		const categoryIds = new Set();
		const categories = this.#data.map(p => p.categories).flat(1);
		for (const category of categories) {
			if (!categoryIds.has(category.id)) {
				this.#categories.push(category);
			}
			categoryIds.add(category.id);
		}
	}

	#initialiseMarkers() {
		this.#categories.forEach(c => this.#markerGroups[c.id] = []);
	}

	#getFeaturedBlogID() {
		this.#data.forEach((datum) => {
			datum.tags.forEach((tag) => {
				if (tag.title === 'Featured') {
					this.#featuredBlogID = datum.id;
				}
			});
		});
		this.#featuredBlogID = this.#featuredBlogID ? this.#featuredBlogID : this.#data[0].id;
	}

	renderCategoryItems() {
		this.#categoryListParentElement.innerHTML = this.#currentCategoryIconElement + '<a class="category-item active" data-bs-toggle="collapse" href="#category-0">All</a>';
		if (this.#type === CategoryFilter.FAQS_TYPE) {
			let categoryList = [];
			this.#data.forEach((datum) => {
				if (datum.name && datum.content) {
					this.#allCategoryContent += '<li>' + '<div class="faq-question">' + datum.name + '</div>' + '<div class="faq-answer">' + datum.content + '</div>' + '</li>';
					if (categoryList.find(item => item.category.id === datum.categories.id)) {
						categoryList.find(item => item.category.id === datum.categories.id).content += '<li>' + '<div class="faq-question">' + datum.name + '</div>' + '<div class="faq-answer">' + datum.content + '</div>' + '</li>';
					} else {
						categoryList.push({
							category: datum.categories,
							content: '<li>' + '<div class="faq-question">' + datum.name + '</div>' + '<div class="faq-answer">' + datum.content + '</div>' + '</li>'
						});
					}
				}
			});
			this.#categoryContentParentElement.innerHTML = '<div class="collapse show" id="category-0"><ul>' + this.#allCategoryContent + '</ul></div>';
			categoryList.forEach((category) => {
				if (category.category.name) {
					this.#categoryListParentElement.innerHTML += '<a class="category-item" data-bs-toggle="collapse" href="#category-' + category.category.id + '">' + category.category.name + '</a>';
					this.#categoryContentParentElement.innerHTML += '<div class="collapse" id="category-' + category.category.id + '"><ul>' + category.content + '</ul></div>';
				}
			});
		} else {
			this.#categoryContentParentElement.innerHTML = '<p class="collapse show" id="category-0">' + this.#allCategoryContent + '</p>';
			this.#categories.forEach((category) => {
				let content = category.content ? category.content : '';
				this.#categoryListParentElement.innerHTML += '<a class="category-item" data-bs-toggle="collapse" href="#category-' + category.id + '">' + category.name + '</a>';
				this.#categoryContentParentElement.innerHTML += '<div class="collapse" id="category-' + category.id + '">' + content + '</div>';
			});
		}
	}

	#handleCategoryItems() {
		this.#holderElement.querySelectorAll('.category-item').forEach((categoryItem) => {
			categoryItem.addEventListener('click', (event) => {
				event.preventDefault();
				this.#setCollapseStatus(categoryItem);

				if (this.#type === CategoryFilter.PLACES_TYPE) {
					this.#setMarkersVisibleStatusByCategory(categoryItem);
				}

				if (this.#type !== CategoryFilter.FAQS_TYPE) {
					this.#clearPreviewBlocks();
					this.#clearInfoBoxTriggers();

					let categoryID = categoryItem.getAttribute('href').split('#category-')[1].toString();
					this.#data.forEach((datum) => {
						if (categoryID === '0') {
							this.#setPreviewBlocks(datum);
						} else {
							datum.categories.forEach((category) => {
								if (category.id.toString() === categoryID) {
									this.#setPreviewBlocks(datum);
								}
							});
						}
						this.#setInfoBoxTriggers();
					});
				}
			});
		});
	}

	/**
	 * @param {HTMLElement} categoryItem
	 */
	#setCollapseStatus(categoryItem) {
		this.#holderElement.querySelector('.current-category-icon').remove();
		for (const item of this.#holderElement.querySelectorAll('.category-item')) {
			item.classList.remove('active');
		}

		categoryItem.insertAdjacentHTML('beforebegin', this.#currentCategoryIconElement);
		categoryItem.classList.add('active');

		for (const contentItem of this.#holderElement.querySelectorAll('.collapse')) {
			contentItem.classList.remove('show');
		}
	}

	/**
	 * @param {HTMLElement} categoryItem
	 */
	#setMarkersVisibleStatusByCategory(categoryItem) {
		let categoryId = Number(categoryItem.getAttribute('href').split('#category-')[1]);

		this.#categories.forEach((category) => {
			for (let marker of this.#markerGroups[category.id]) {
				marker.setVisible(false);
			}
		});
		for (let marker of this.#markerGroups[categoryId]) {
			marker.setVisible(true);
		}
	}

	/**
	 * @param {HTMLElement} previewBlockItem
	 */
	#setMarkersVisibleStatusByPreview(previewBlockItem) {
		this.#markerGroups[0].forEach((marker) => {
			marker.setVisible(marker.id.toString() === previewBlockItem.getAttribute('id').toString());
		});
	}

	#handleData() {
		this.#data.forEach((datum) => {
			if (this.#type === CategoryFilter.PLACES_TYPE) {
				this.#setMarkers(datum);
			}
			if (this.#type !== CategoryFilter.FAQS_TYPE) {
				this.#setPreviewBlocks(datum);
			}
		});
		if (this.#type !== CategoryFilter.FAQS_TYPE) {
			this.#setInfoBoxTriggers();
		}
	}

	/**
	 * @param {any} datum
	 */
	#setMarkers(datum) {
		const position = new google.maps.LatLng(datum.latitude, datum.longitude);
		let marker = new google.maps.Marker({
			// id: datum.id,
			position: position,
			map: this.#googleMap,
			icon: window.config.themeDirectory + '/images/svg/marker.svg'
		});

		datum.categories.forEach(c => {
			this.#markerGroups[c.id].push(marker);
			this.#markerGroups[0].includes(marker) ? this.#markerGroups[0].push() : this.#markerGroups[0].push(marker);
		});
		this.#bounds.extend(position);
		this.#googleMap.fitBounds(this.#bounds, {top: 63, right: 63, bottom: 63, left: 63});

		this.#setInfoBoxes(datum, marker);
	}

	/**
	 * @param {any} datum
	 * @param {google.maps.Marker} marker
	 */
	#setInfoBoxes(datum, marker) {
		let address = datum.address? '<p>' + datum.address + '</p>' : '',
			phone = datum.phone? '<a href="tel:' + datum.phone + '">' + datum.phone + '</a>' : '',
			email = datum.email? '<a href="mailto:' + datum.email + '">' + datum.email + '</a>' : '',
			website = datum.website? '<a href="' + datum.website + '" target="_blank" class="website-url">' + datum.website + '</a>' : '',
			infoBox = new SnazzyInfoWindow({
				marker: marker,
				content: '<h4>' + datum.name + '</h4>' + address + phone + email + website,
				closeOnMapClick: true,
				closeWhenOthersOpen: true
			});

		marker.addListener('click', function() {
			infoBox.open();
		});
	}

	/**
	 * @param {any} datum
	 */
	#setPreviewBlocks(datum) {
		if (this.#type === CategoryFilter.BLOG_TYPE && datum.id === this.#featuredBlogID) {} else {
			this.#previewBlockItemIndex ++;
			this.#newPreviewBlockRowElement = '<div class="row preview-block-row preview-block-row-' + this.#previewBlockRowIndex.toString() + '"></div>';
			this.#newInfoBoxRowElement = '<div class="row preview-info-box-row preview-info-box-row-' + this.#previewBlockRowIndex.toString() + '"></div>';

			let infoBoxRowElement,
				infoBoxItemElement,
				infoBoxElement,
				tags = '';
			datum.categories.forEach((category) => {
				tags += '<span id="' + category.id + '">' + category.name + '</span>';
			});
			let previewBlockElement = '<div class="col-12 col-xl-3 col-lg-3 col-md-6 col-sm-12 col-xs-12 d-grid preview-block-' + this.#previewBlockItemIndex + '" id="' + datum.id + '">' +
				'	<div class="preview-block">' +
				`		<img src="${datum.image ? datum.image.md : ''}" alt="${datum.image ? datum.image.alt : ''}">` +
				'   	<div class="overlay">' +
				'			<h5>' + datum.name + '</h5>' +
				'			<div class="link-wrapper">' +
				'   			<div class="tag-wrapper">' + tags + '</div>' +
				'				<img src="' + window.config.themeDirectory + '/images/svg/arrow-top-light.svg" alt="Aarow icon" class="link-icon">' +
				'			</div>' +
				'   	</div>' +
				'	</div>' +
				'</div>';
			if (this.#type === CategoryFilter.PLACES_TYPE) {
				let content = datum.content ? '<p>' + datum.content + '</p>' : '',
					subtitle = datum.address || datum.phone || datum.email ? '<h6>Details</h6>' : '',
					address = datum.address ? '<p>Locations</p>' +
					'							<p>' + datum.address + '</p>' +
					'							<br>' : '',
					phone = datum.phone ? '<p>Phone</p>' +
					'						<p>' + datum.phone + '</p>' +
					'						<br>' : '',
					email = datum.email ? '<p>Email</p>' +
					'						<p>' + datum.email + '</p>' : '',
					website = datum.website ? '<div class="col col-2 info-box-button-group">' +
					'								<a href="' + datum.website + '" class="btn btn-dark" target="_blank">Visit site</a>' +
					'							</div>' : '';
					infoBoxElement = '<div class="col preview-info-box preview-info-box-' + this.#previewBlockItemIndex + '">' +
					'	<div class="info-box-wrapper">' +
					'		<button class="btn btn-close"><img src="' + window.config.themeDirectory + '/images/svg/sprites/times.svg" alt="Close icon" class="close-icon"></button>' +
					'		<div class="row">' +
					'			<div class="col col-6 me-5 pe-5 info-box-content">' +
					'				<h6>' + datum.name + '</h6>' + content +
					'			</div>' +
					'			<div class="col info-box-sub-content">' + subtitle + address + phone + email +
					'			</div>' + website +
					'		</div>' +
					'	</div>' +
					'</div>';
				if (this.#maxPreviewBlocksInRow === 1) {
					content = datum.content ? datum.content : '';
					address = datum.address ? '<strong>Locations</strong>' +
					'							<p>' + datum.address + '</p>' : '';
					phone = datum.phone ? '<strong>Phone</strong>' +
					'						<p>' + datum.phone + '</p>' : '';
					email = datum.email ? '<strong>Email</strong>' +
					'						<p>' + datum.email + '</p>' : '';
					website = datum.website ? '<a href="' + datum.website + '" class="btn btn-dark" target="_blank">Visit site</a>' : '';
					infoBoxElement = '<div class="col preview-info-box preview-info-box-' + this.#previewBlockItemIndex + '">' +
					'	<div class="info-box-wrapper">' +
					'		<button class="btn btn-close"><img src="' + window.config.themeDirectory + '/images/svg/sprites/times.svg" alt="Close icon" class="close-icon"></button>' +
					'		<div class="row">' +
					'			<div class="col">' +
					'				<h6>' + datum.name + '</h6>' + content + subtitle + address + phone + email + website +
					'			</div>' +
					'		</div>' +
					'	</div>' +
					'</div>'
				}
			} else if (this.#type === CategoryFilter.OFFER_TYPE) {
				let content = datum.content ? '<p>' + datum.content + '</p>' : '',
					subtitle = datum.start || datum.end ? '<h6>Valid Dates</h6>' : '',
					startDate = datum.start ? DateFormat.format(new Date(datum.start), DateFormat.OFFERS) : false,
					endDate = datum.end ? DateFormat.format(new Date(datum.end), DateFormat.OFFERS) : false,
					date = startDate && endDate ? '<p>' + startDate + ' to ' + endDate + '</p>' : startDate ? '<p>Starts from ' + startDate + '</p>' : endDate ? '<p>Ends on ' + endDate + '</p>' : '',
					bookOffer = datum.website ? '<a href="' + datum.website + '" class="btn btn-primary">Book offer now</a>' : '',
					moreInfo = datum.link ? '<a href="' + datum.link + '" class="btn btn-dark">More Info</a>': '',
					website = '<div class="col col-2 info-box-button-group">' + bookOffer + moreInfo + '</div>';
				infoBoxElement = '<div class="col preview-info-box preview-info-box-' + this.#previewBlockItemIndex + '">' +
					'	<div class="info-box-wrapper">' +
					'		<button class="btn btn-close"><img src="' + window.config.themeDirectory + '/images/svg/sprites/times.svg" alt="Close icon" class="close-icon"></button>' +
					'		<div class="row">' +
					'			<div class="col col-6 me-5 pe-5 info-box-content">' +
					'				<h6>' + datum.name + '</h6>' + content +
					'			</div>' +
					'			<div class="col info-box-sub-content">' + subtitle + date +
					'			</div>' + website +
					'		</div>' +
					'	</div>' +
					'</div>';
				if (this.#maxPreviewBlocksInRow === 1) {
					content = datum.content ? datum.content : '';
					bookOffer = datum.website ? '<a href="' + datum.website + '" class="btn btn-primary">Book offer now</a>' : '',
					moreInfo = datum.link ? '<a href="' + datum.link + '" class="btn btn-dark">More Info</a>': '';
					infoBoxElement = '<div class="col preview-info-box preview-info-box-' + this.#previewBlockItemIndex + '">' +
						'	<div class="info-box-wrapper">' +
						'		<button class="btn btn-close"><img src="' + window.config.themeDirectory + '/images/svg/sprites/times.svg" alt="Close icon" class="close-icon"></button>' +
						'		<div class="row">' +
						'			<div class="col">' +
						'				<h6>' + datum.name + '</h6>' + content  + subtitle + date + bookOffer + moreInfo +
						'			</div>' +
						'		</div>' +
						'	</div>' +
						'</div>';
				}
			} else {
				let content = datum.content ? '<p>' + datum.content + '</p>' : '',
					website = datum.website ? '	<div class="col col-2">' +
					'								<a href="' + datum.website + '" class="btn btn-dark">Read more</a>' +
					'							</div>' : '';
					infoBoxElement = '<div class="col preview-info-box preview-info-box-' + this.#previewBlockItemIndex + '">' +
					'	<div class="info-box-wrapper">' +
					'		<button class="btn btn-close"><img src="' + window.config.themeDirectory + '/images/svg/sprites/times.svg" alt="Close icon" class="close-icon"></button>' +
					'		<div class="row">' +
					'			<div class="col me-5 pe-5 info-box-content">' +
					'				<h6>' + datum.name + '</h6>' + content +
					'			</div>' + website +
					'		</div>' +
					'	</div>' +
					'</div>';
				if (this.#maxPreviewBlocksInRow === 1) {
					website = datum.website ? '<a href="' + datum.website + '" class="btn btn-dark">Read more</a>' : '';
					infoBoxElement = '<div class="col preview-info-box preview-info-box-' + this.#previewBlockItemIndex + '">' +
					'	<div class="info-box-wrapper">' +
					'		<button class="btn btn-close"><img src="' + window.config.themeDirectory + '/images/svg/sprites/times.svg" alt="Close icon" class="close-icon"></button>' +
					'		<div class="row">' +
					'			<div class="col">' +
					'				<h6>' + datum.name + '</h6>' + content + website +
					'			</div>' +
					'		</div>' +
					'	</div>' +
					'</div>';
				}
			}

			if (this.#maxPreviewBlocksInRow > 1) {
				if (this.#previewBlockItemIndex < this.#maxPreviewBlocksInRow) {
					if (this.#previewBlockItemIndex === 1 && this.#previewBlockRowIndex > 0) {
						this.#selectedInfoBoxRowElement.insertAdjacentHTML('afterend', this.#newPreviewBlockRowElement);
						this.#selectedPreviewBlockRowElement = this.#holderElement.querySelector('.preview-block-row-' + this.#previewBlockRowIndex.toString());
						this.#selectedPreviewBlockRowElement.insertAdjacentHTML('afterend', this.#newInfoBoxRowElement);
						this.#selectedInfoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + this.#previewBlockRowIndex.toString());
					}
				} else {
					this.#previewBlockItemIndex = 0;
					this.#previewBlockRowIndex ++;
				}
				this.#selectedPreviewBlockRowElement.innerHTML += previewBlockElement;
				this.#selectedInfoBoxRowElement.innerHTML += infoBoxElement;
				if (this.#previewBlockItemIndex === 0) {
					infoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + (this.#previewBlockRowIndex - 1).toString());
					infoBoxItemElement = infoBoxRowElement.querySelector('.preview-info-box-' + this.#maxPreviewBlocksInRow.toString());
					this.#previewBlockStorage.push({'rowIndex': this.#previewBlockRowIndex - 1, 'itemIndex': this.#maxPreviewBlocksInRow, 'height': infoBoxItemElement.scrollHeight});
				} else {
					infoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + this.#previewBlockRowIndex.toString());
					infoBoxItemElement = infoBoxRowElement.querySelector('.preview-info-box-' + this.#previewBlockItemIndex.toString());
					this.#previewBlockStorage.push({'rowIndex': this.#previewBlockRowIndex, 'itemIndex': this.#previewBlockItemIndex, 'height': infoBoxItemElement.scrollHeight});
				}
			} else {
				if (this.#previewBlockRowIndex > 0) {
					this.#selectedInfoBoxRowElement.insertAdjacentHTML('afterend', this.#newPreviewBlockRowElement);
					this.#selectedPreviewBlockRowElement = this.#holderElement.querySelector('.preview-block-row-' + this.#previewBlockRowIndex.toString());
					this.#selectedPreviewBlockRowElement.insertAdjacentHTML('afterend', this.#newInfoBoxRowElement);
					this.#selectedInfoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + this.#previewBlockRowIndex.toString());
				}

				this.#selectedPreviewBlockRowElement.innerHTML += previewBlockElement;
				this.#selectedInfoBoxRowElement.innerHTML += infoBoxElement;
				infoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + this.#previewBlockRowIndex.toString());
				infoBoxItemElement = infoBoxRowElement.querySelector('.preview-info-box-' + this.#previewBlockItemIndex.toString());
				this.#previewBlockStorage.push({'rowIndex': this.#previewBlockRowIndex, 'itemIndex': this.#previewBlockItemIndex, 'height': infoBoxItemElement.scrollHeight});

				this.#previewBlockItemIndex = 0;
				this.#previewBlockRowIndex ++;
			}
			infoBoxItemElement.style.display = 'none';
			infoBoxItemElement.style.height = 0;
		}
	}

	#clearPreviewBlocks() {
		this.#previewBlockRowIndex = 0;
		this.#previewBlockItemIndex = 0;
		this.#previewBlockStorage = []
		this.#selectedPreviewBlockRowElement = this.#holderElement.querySelector('.preview-block-row-' + this.#previewBlockRowIndex.toString());
		this.#selectedInfoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + this.#previewBlockRowIndex.toString());
		this.#holderElement.querySelectorAll('.preview-block-row').forEach((element) => {
			if (element.classList.contains('preview-block-row-0')) {
				element.innerHTML = '';
			} else {
				element.remove();
			}
		});
	}

	#setInfoBoxTriggers() {
		this.#previewBlockStorage.forEach((item) => {
			let previewBlockRowElement = this.#holderElement.querySelector('.preview-block-row-' + item.rowIndex.toString()),
				previewBlockItemElement = previewBlockRowElement.querySelector('.preview-block-' + item.itemIndex.toString()),
				infoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + item.rowIndex.toString()),
				infoBoxItemElement = infoBoxRowElement.querySelector('.preview-info-box-' + item.itemIndex.toString()),
				closeBtn = infoBoxItemElement.querySelector('.btn-close'),
				self = this;

			previewBlockItemElement.addEventListener('click', function() {
				self.#hideAllInfoBoxes();
				infoBoxItemElement.style.display = 'block';
				infoBoxRowElement.style.paddingBottom = '1.3rem';
				infoBoxItemElement.style.height = item.height.toString() + 'px';
				infoBoxItemElement.scrollIntoView();
				if (self.#type === CategoryFilter.PLACES_TYPE) {
					self.#setMarkersVisibleStatusByPreview(previewBlockItemElement);
				}

			});

			closeBtn.addEventListener("click", function() {
				infoBoxItemElement.style.display = 'none';
				infoBoxRowElement.style.paddingBottom = '0';
				infoBoxItemElement.style.height = 0;
				if (self.#type === CategoryFilter.PLACES_TYPE) {
					self.#setMarkersVisibleStatusByCategory(self.#holderElement.querySelectorAll('.category-item')[0]);
				}

			});
		});
	}

	#clearInfoBoxTriggers() {
		this.#holderElement.querySelectorAll('.preview-info-box-row').forEach((element) => {
			if (element.classList.contains('preview-info-box-row-0')) {
				element.innerHTML = null;
			} else {
				element.remove();
			}
		});
	}

	#hideAllInfoBoxes() {
		this.#previewBlockStorage.forEach((item) => {
			let infoBoxRowElement = this.#holderElement.querySelector('.preview-info-box-row-' + item.rowIndex.toString()),
				infoBoxItemElement = infoBoxRowElement.querySelector('.preview-info-box-' + item.itemIndex.toString());

			infoBoxItemElement.style.display = 'none';
			infoBoxRowElement.style.paddingBottom = '0';
			infoBoxItemElement.style.height = 0;
		});
	}
}
